/**
 * ConsentDialog - Beta user data consent modal
 * Shows on first login before user can access the app
 */
import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Shield, 
  Database, 
  Eye, 
  Lock,
  CheckCircle2,
  Loader2,
  FileText,
  AlertTriangle
} from 'lucide-react';

const ConsentDialog = ({ open, onConsent, onDecline }) => {
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleConsent = async () => {
    if (!agreed) return;
    setLoading(true);
    const success = await onConsent();
    if (!success) {
      setLoading(false);
    }
  };

  const dataUsagePoints = [
    {
      icon: Database,
      title: 'Data Storage',
      description: 'Your procurement data is stored securely in MongoDB Atlas with encryption.'
    },
    {
      icon: Eye,
      title: 'Data Processing',
      description: 'Your inputs are processed by AI models (OpenAI/Anthropic/Google) to generate RFPs and risk intelligence.'
    },
    {
      icon: Lock,
      title: 'Data Privacy',
      description: 'Your data is not sold or shared with third parties. It is used only to provide ProcMatrix services.'
    },
    {
      icon: FileText,
      title: 'Beta Feedback',
      description: 'As a beta user, your usage patterns and feedback help us improve ProcMatrix.'
    }
  ];

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" hideCloseButton>
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-[#0055FF]/10 rounded-lg">
              <Shield className="w-6 h-6 text-[#0055FF]" />
            </div>
            <div>
              <DialogTitle className="text-xl">Beta Program Consent</DialogTitle>
              <DialogDescription className="text-sm">
                Please review and accept before continuing
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {/* Beta Notice */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-900">Beta Version</p>
                <p className="text-xs text-amber-700 mt-1">
                  ProcMatrix is currently in beta. Features may change, and we appreciate your patience and feedback.
                </p>
              </div>
            </div>
          </div>

          {/* Data Usage Points */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-zinc-900">How We Use Your Data</h3>
            {dataUsagePoints.map((point, index) => (
              <div key={index} className="flex items-start gap-3">
                <div className="p-1.5 bg-zinc-100 rounded-lg flex-shrink-0">
                  <point.icon className="w-4 h-4 text-zinc-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-zinc-900">{point.title}</p>
                  <p className="text-xs text-zinc-600">{point.description}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Consent Checkbox */}
          <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Checkbox
                id="consent"
                checked={agreed}
                onCheckedChange={setAgreed}
                className="mt-0.5"
                data-testid="consent-checkbox"
              />
              <label htmlFor="consent" className="text-sm text-zinc-700 cursor-pointer leading-relaxed">
                I understand and agree that my data will be processed as described above. I consent to participate in the ProcMatrix beta program and acknowledge that this is a demo environment.
              </label>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col gap-3">
            <Button
              onClick={handleConsent}
              disabled={!agreed || loading}
              className="w-full bg-[#0055FF] hover:bg-[#0044CC] text-white"
              data-testid="consent-accept-btn"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle2 className="w-4 h-4 mr-2" />
              )}
              Accept & Continue
            </Button>
            <Button
              onClick={onDecline}
              variant="ghost"
              className="w-full text-zinc-500 hover:text-zinc-700"
              data-testid="consent-decline-btn"
            >
              Decline & Sign Out
            </Button>
          </div>

          {/* Contact */}
          <p className="text-xs text-zinc-500 text-center">
            Questions? Contact{' '}
            <a href="mailto:ashokr@sahinta.com" className="text-[#0055FF] hover:underline">
              ashokr@sahinta.com
            </a>
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ConsentDialog;
